import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { GoogleButton } from '@/components/auth/GoogleButton';
import { GitHubButton } from '@/components/auth/GitHubButton';
import { useAuth } from '@/contexts/AuthContext';

interface LoginPageProps {
  features?: {
    oauth?: boolean;
    google_oauth?: boolean;
    github_oauth?: boolean;
    password_login?: boolean;
  };
  branding?: {
    name?: string;
    website_domain?: string;
  };
}

export function LoginPage({ 
  features = { 
    oauth: true,
    google_oauth: true,
    github_oauth: true,
    password_login: true,
  },
  branding = {
    name: 'doploy',
    website_domain: 'doploy.io',
  },
}: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await login({ email, password });
      
      if (!result.success) {
        setError(result.error || 'An error occurred. Please try again.');
        setIsLoading(false);
        return;
      }
      
      if (result.requires_2fa) {
        // Redirect to 2FA page
        navigate('/auth/verify-2fa');
        return;
      }
      
      // Success - navigation is handled by auth context
    } catch {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Title */}
      <h1 className="text-xl font-semibold text-gray-900 mb-6">
        Login to your account
      </h1>

      {/* Login Form */}
      {features.password_login && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <div className="text-right">
            <Link 
              to="/auth/forgot-password" 
              className="text-sm text-primary hover:underline"
            >
              Help! I forgot my password
            </Link>
          </div>

          <Button 
            type="submit" 
            className="w-full h-11" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </Button>
        </form>
      )}

      {/* Divider */}
      {features.oauth && features.password_login && (
        <div className="relative my-6">
          <Separator />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
            or continue with
          </span>
        </div>
      )}

      {/* OAuth Buttons */}
      {features.oauth && (
        <div className="space-y-3">
          {features.google_oauth && (
            <GoogleButton text="Sign in with Google" />
          )}
          {features.github_oauth && (
            <GitHubButton text="Sign in with GitHub" />
          )}
        </div>
      )}

      {/* Sign Up Link */}
      <div className="mt-6 text-center">
        <span className="text-muted-foreground">Don't have an account yet?</span>
        <Link to="/auth/signup">
          <Button variant="default" className="w-full mt-3">
            Sign Up
          </Button>
        </Link>
      </div>

      {/* Divider */}
      <Separator className="my-6" />

      {/* New Visitor Card */}
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-white shadow-sm">
        <CardContent className="p-6 text-center">
          <h3 className="font-semibold text-gray-900 mb-2">
            New to {branding.name}?
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Discover how {branding.name} makes deploying and managing your Odoo instances simple, fast, and reliable.
          </p>
          <Button 
            variant="outline" 
            className="w-full border-primary/30 hover:bg-primary/5 hover:border-primary/50"
            onClick={() => window.open(`https://${branding.website_domain}`, '_blank')}
          >
            <Rocket className="h-4 w-4 mr-2" />
            Learn More About {branding.name}
          </Button>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
